import { ConditionalWrap } from '@libs/ui/ds'
import { useCommonAppTranslation } from '@libs/utils/translations'
import { useTrackScheduleFeaturesInteraction } from '@spa/core/externals/segment'
import { LinkWrapper } from '@spa/core/router'
import { useMemoSelector } from '@spa/redux/hooks'
import { defaultMembershipTab } from '@spa/scenes/MembershipPage/defaultMembershipTab'
import {
  membershipsSelectors,
  usePlanningPolicies
} from '@spa/scenes/Plannings/redux/selectors'
import type { FC } from 'react'
import React from 'react'
import { getCurrentMembershipSelector } from 'snap-redux/modules/memberships/selectors'
import { getRoleRights } from 'snap-redux/modules/memberships/utils'

import styles from './EmployeeLink.module.css'

type EmployeeLinkProps = {
  membershipId: number
}

const EmployeeLink: FC<EmployeeLinkProps> = ({ membershipId }) => {
  const { t } = useCommonAppTranslation()
  const scheduleTracker = useTrackScheduleFeaturesInteraction()

  const currentMembership = useMemoSelector(getCurrentMembershipSelector)
  const membership = membershipsSelectors.useGetCachedById(membershipId)

  const policies = usePlanningPolicies()

  /**
   * Based on member role and current user permissions
   * returns a boolean that allow to navigate to the member profile
   * Order is important
   */
  const hasPermission = membership => {
    const { atLeast } = getRoleRights(membership)

    switch (true) {
      case atLeast.owner:
        return policies.canSeeAdminProfile
      case atLeast.director:
        return policies.canSeeDirectorProfile
      case atLeast.manager:
        return policies.canSeeManagerProfile
      case !atLeast.manager:
      default:
        return policies.canSeeEmployeeProfile
    }
  }

  /**
   * Employees can always see their own profile
   * They can see other employees profile based on permissions
   */
  const hasAccessToProfile =
    currentMembership?.id === membership?.id || hasPermission(membership)

  return (
    <ConditionalWrap
      condition={hasAccessToProfile}
      wrap={children => (
        <LinkWrapper
          toRoute={{
            route: 'member',
            pathParams: {
              membership_id: membershipId,
              view: defaultMembershipTab
            }
          }}
          onClick={scheduleTracker.employeeMemberpageOpened}
        >
          {children}
        </LinkWrapper>
      )}
    >
      <div className={styles.Container}>
        {t('models.fullname', {
          value: {
            firstname: membership?.firstname,
            lastname: membership?.lastname
          }
        })}
      </div>
    </ConditionalWrap>
  )
}

export default React.memo(EmployeeLink)
